import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { EMPTY_VALUE, labelForRole } from '../internalRoles';

/**
 * Transforms a user summary API model to a view entity
 *
 * @param {import('../types/User.api').IUserSummaryApiModel} model - API model
 * @returns {import('../types/User').IUserSummaryViewEntity} View entity
 */
const toUserSummaryViewEntity = (model) => ({
  id: model.id,
  email: model.email,
  emailStatus: model.emailStatus,
  phoneNumber: model.phoneNumber,
  userStatus: model.userStatus,
  userType: model.userType,
  source: model.source,
  lastActiveAt: model.lastActiveAt,
  createdAt: model.createdAt,
  updatedAt: model.updatedAt,
  firstName: model.firstName,
  lastName: model.lastName,
  timeZone: model.timeZone,
});

/**
 * Transforms API response to users result
 *
 * @param {import('../types/User.api').IGetUsersByIdsApiResponse} response - API response
 * @returns {import('../types/User').IGetUsersByIdsResult} Users result
 */
const toGetUsersByIdsResult = (response) => ({
  data: response.data.map(toUserSummaryViewEntity),
});

/**
 * Transforms an internal-user API model to a view entity.
 *
 * The `userId` -> `id` mapping is the whole point of this adapter and is the
 * one thing every caller depends on. An internal-user row carries two ids:
 * `id` is the internal_user row's own primary key, while `userId` is the Mongo
 * ObjectId of the user record. Consumers feed the value on through to
 * `PATCH admin/users/:id/dsoManager` and to the job-list `recruiter` filter,
 * both of which address the user record — so picking up the row key would
 * render a correct-looking dropdown that silently fails to assign or filter.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {import('../types/User').IInternalUserViewEntity} View entity
 */
const toInternalUserViewEntity = (model) => ({
  id: model.userId,
  role: model.role,
  email: model.email,
  firstName: model.firstName,
  lastName: model.lastName,
});

/**
 * Transforms the paginated internal-users envelope to a role-holder list.
 *
 * Defaults to `[]` rather than passing undefined through: an undefined list
 * re-arms the `if (!dsoManagerList)` refetch guard in DNTCustomFilters, which
 * is an unbounded fetch loop.
 *
 * @param {import('../types/User.api').IGetInternalUsersApiResponse} response - API response
 * @returns {import('../types/User').IGetInternalUsersResult} Internal users result
 */
const toGetInternalUsersResult = (response) => ({
  data: (response?.data ?? []).map(toInternalUserViewEntity),
});

/**
 * "First Last" for an internal user, or '' when it has neither — both name
 * fields are nullable upstream, so each caller picks its own fallback.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {string} Display name
 */
const internalUserName = (model) =>
  [model.firstName, model.lastName].filter(Boolean).join(' ').trim();

/**
 * The display name for a table row: as above, or an em dash when there is none.
 *
 * @param {{ firstName?: string | null, lastName?: string | null }} model
 * @returns {string}
 */
const toDisplayName = (model) => internalUserName(model) || EMPTY_VALUE;

/**
 * Transforms an internal-user API model to a GoTu Admin table row.
 *
 * Distinct from `toInternalUserViewEntity` because the two answer different
 * questions: that one feeds dropdowns whose value is passed to endpoints
 * addressing the user record, this one feeds a read-only table. Both take `id`
 * from `userId` so the domain has one meaning of `id` throughout.
 *
 * `name` is assembled here rather than in the cell: first_name and last_name
 * are both nullable upstream (the ADM backfill swept in every admin regardless
 * of name state), so a row can legitimately have neither and the table needs
 * something to render. Email is NOT NULL upstream and is the fallback identity.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {import('../types/User').IInternalUserRowViewEntity} View entity
 */
const toInternalUserRowViewEntity = (model) => ({
  id: model.userId,
  name: toDisplayName(model),
  email: model.email || EMPTY_VALUE,
  role: model.role,
  roleLabel: labelForRole(model.role),
});

/**
 * Transforms the paginated internal-users envelope into a page of table rows.
 *
 * `total` is the server's count before the page limit, which the header count
 * and the pagination stepper both read. It defaults to 0 rather than undefined
 * so `useClampedPage` sees a real total and the stepper renders.
 *
 * @param {import('../types/User.api').IGetInternalUsersApiResponse} response - API response
 * @returns {import('../types/User').IGetInternalUsersPageResult} Page of rows
 */
const toGetInternalUsersPageResult = (response) => ({
  data: (response?.data ?? []).map(toInternalUserRowViewEntity),
  total: response?.total ?? 0,
});

/**
 * Transforms an internal-user API model to an option in the Sales Metros admin
 * picker (GOTU-6009).
 *
 * **The one adapter in this domain keyed by the row `id`, not `userId`** — and
 * named `internalUserId` rather than `id` so neither meaning can pass for the
 * other. The adapters above address monolith endpoints, which take the user
 * record's id. This one feeds organization-service's
 * `PUT admin/sales-metros/{id}/admins`, which validates every id against its
 * mirror of auth-service's internal_user table, and that mirror is keyed by the
 * row id (auth-service `BuildInternalUserEntity` keys it by `iu.ID`). A metro's
 * `assignedInternalUserIds` therefore carries row ids, and sending a `userId`
 * here is a 400 "not a current GoTu admin".
 *
 * `name` falls back to the email for the same reason as the table row above:
 * both name fields are nullable upstream, and email is NOT NULL.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {import('../types/User').IGotuAdminOptionViewEntity} View entity
 */
const toGotuAdminOptionViewEntity = (model) => {
  const name = internalUserName(model);

  return {
    internalUserId: model.id,
    name: name || model.email || EMPTY_VALUE,
  };
};

/**
 * A stored phone number as an admin reads it.
 *
 * auth-service stores E.164. North American numbers render in national format,
 * "(305) 555-0123"; any other country keeps its calling code, which national
 * format would drop. A value that is not a possible number is shown as stored
 * rather than hidden — it is still the number on the account. That includes a
 * wrong-length one, "+1585231901": it parses, and national format would strip
 * the country code and pass it off as a valid shorter number.
 *
 * @param {string | null} phoneNumber
 * @returns {string}
 */
const toPhoneLabel = (phoneNumber) => {
  if (!phoneNumber) return EMPTY_VALUE;

  const parsed = parsePhoneNumberFromString(phoneNumber, { defaultCountry: 'US', extract: false });
  if (!parsed?.isPossible()) return phoneNumber;

  return parsed.countryCallingCode === '1' ? parsed.formatNational() : parsed.formatInternational();
};

/**
 * When a user was last active, as "Oct 22, 2026, 3:04 PM" in the admin's
 * timezone. The time is kept because the value moves within a day — every
 * token refresh updates it — and a date alone cannot tell two of those apart.
 *
 * A user who has never been active arrives as a sentinel, not as null: the
 * Unix epoch on every user auth-service migrated without a sign-in, or Go's
 * zero time (year 1) from the non-pointer `time.Time`. Real activity is
 * written as the time it happened, so anything at or before the epoch is one
 * of those and renders as the em dash.
 *
 * @param {string} lastActiveAt - ISO 8601
 * @returns {string}
 */
const toLastActiveLabel = (lastActiveAt) => {
  if (!lastActiveAt) return EMPTY_VALUE;

  const date = new Date(lastActiveAt);
  if (Number.isNaN(date.getTime()) || date.getTime() <= 0) return EMPTY_VALUE;

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Transforms an auth-service user to a Location Users table row.
 *
 * Every label is resolved here because every one of these fields can be
 * missing on a real row: the list includes accounts that stopped part-way
 * through sign-up, which may have no name, no email or no phone yet.
 *
 * @param {import('../types/User.api').IUserSummaryApiModel} model - API model
 * @returns {import('../types/User').ILocationUserRowViewEntity} View entity
 */
const toLocationUserRowViewEntity = (model) => ({
  id: model.id,
  name: toDisplayName(model),
  email: model.email || EMPTY_VALUE,
  phoneLabel: toPhoneLabel(model.phoneNumber),
  lastActiveLabel: toLastActiveLabel(model.lastActiveAt),
});

/**
 * Transforms a page of the user search into a page of Location Users rows.
 *
 * `total` defaults to 0 for the reason `toGetInternalUsersPageResult` gives.
 *
 * @param {import('../types/User.api').IUsersSearchApiResponse} response - API response
 * @returns {import('../types/User').IGetLocationUsersPageResult} Page of rows
 */
const toGetLocationUsersPageResult = (response) => ({
  data: (response?.data ?? []).map(toLocationUserRowViewEntity),
  total: response?.total ?? 0,
});

/**
 * Indexes organization-service's location counts by identity id, which is how
 * the table joins them onto the auth-service rows.
 *
 * @param {import('../types/User.api').IGetIdentityLocationCountsApiResponse} response
 * @returns {Object<string, number>} Location count per identity id
 */
const toLocationCountsById = (response) =>
  Object.fromEntries(
    (response?.items ?? []).map((item) => [item.identityId, item.locationCount]),
  );

/**
 * Transforms a child office API model to a user view entity
 * Maps snake_case fields to camelCase
 *
 * @param {import('../types/User.api').IChildOfficeApiModel} model - API model (snake_case)
 * @returns {import('../types/User').IUserViewEntity} View entity (camelCase)
 */
const toChildOfficeViewEntity = (model) => ({
  id: model.id,
  averageRating: model.average_rating,
  address:
    model.address?.map((addr) => ({
      formattedAddress: addr.formatted_address,
    })) || [],
  userType: model.user_type,
  officeName: model.office_name,
  accountAdmin: model.account_admin,
  hasW9: model.has_w9,
});

/**
 * Transforms API response to child offices result
 *
 * @param {import('../types/User.api').IGetChildOfficesApiResponse} response - API response (array of offices)
 * @returns {import('../types/User').IGetChildOfficesResult} Child offices result
 */
const toGetChildOfficesResult = (response) => ({
  data: response.map(toChildOfficeViewEntity),
});

/**
 * User Adapters
 * Provides transformation functions between API models and view entities
 */
export const UserAdapters = {
  toUserSummaryViewEntity,
  toGetUsersByIdsResult,
  toInternalUserViewEntity,
  toGetInternalUsersResult,
  toInternalUserRowViewEntity,
  toGetInternalUsersPageResult,
  toGotuAdminOptionViewEntity,
  toLocationUserRowViewEntity,
  toGetLocationUsersPageResult,
  toLocationCountsById,
  toChildOfficeViewEntity,
  toGetChildOfficesResult,
};
